import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * Booking Basket — client-side collection of candidates an employer wants to
 * interview in a single booking submission. Persisted to localStorage so the
 * selection survives page reloads. Max 3 items to match backend
 * MAX_CANDIDATES enforced in Booking.jsx and validated server-side.
 */
const STORAGE_KEY = "adelina.booking_basket";
export const BASKET_MAX = 3;

const BasketContext = createContext(null);

function readInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, BASKET_MAX) : [];
  } catch {
    return [];
  }
}

export function BasketProvider({ children }) {
  const [items, setItems] = useState(readInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
  }, [items]);

  const has = useCallback((id) => items.some((c) => c.id === id), [items]);

  const add = useCallback((candidate) => {
    if (!candidate?.id) return { ok: false, reason: "invalid" };
    setItems((prev) => {
      if (prev.some((c) => c.id === candidate.id)) return prev;
      if (prev.length >= BASKET_MAX) return prev;
      // Store only the fields Booking.jsx + BasketSidebar need — keeps localStorage small.
      const slim = {
        id: candidate.id,
        name: candidate.name,
        role: candidate.role,
        nationality: candidate.nationality,
        photo_url: candidate.photo_url,
        age: candidate.age,
      };
      return [...prev, slim];
    });
    return { ok: true };
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const toggle = useCallback((candidate) => {
    if (!candidate?.id) return { ok: false };
    let didAdd = false;
    setItems((prev) => {
      if (prev.some((c) => c.id === candidate.id)) return prev.filter((c) => c.id !== candidate.id);
      if (prev.length >= BASKET_MAX) return prev;
      didAdd = true;
      return [...prev, {
        id: candidate.id,
        name: candidate.name,
        role: candidate.role,
        nationality: candidate.nationality,
        photo_url: candidate.photo_url,
        age: candidate.age,
      }];
    });
    return { ok: true, didAdd };
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({
    items,
    count: items.length,
    max: BASKET_MAX,
    isFull: items.length >= BASKET_MAX,
    has,
    add,
    remove,
    toggle,
    clear,
  }), [items, has, add, remove, toggle, clear]);

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}

export function useBasket() {
  const ctx = useContext(BasketContext);
  if (!ctx) throw new Error("useBasket must be used within <BasketProvider>");
  return ctx;
}
